import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { trainingMatrixApi } from '@/entities/Training';
import type {
  TrainingMatrixDocumentPrefix,
  TrainingMatrixMetadata,
  TrainingMatrixOrder,
  TrainingMatrixPositionRow,
  TrainingMatrixRevisionInfo,
  TrainingMatrixSearchField,
  TrainingMatrixSort,
} from '@/entities/Training';
import type { OrderOptions } from '@/shared/lib';
import { useInfiniteScrollObserver } from '@/shared/lib';
import { SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';

export interface TrainingMatrixFilters {
  positionId?: number;
  documentPrefix?: TrainingMatrixDocumentPrefix;
}

export const ALL_ITEMS_OPTION_ID = SHOW_ALL_ITEMS_QUANTITY;
const ALL_ITEMS_BATCH_SIZE = 30;

export const MATRIX_QUANTITY_OPTIONS = [
  { id: 10, name: '10 por página' },
  { id: 25, name: '25 por página' },
  { id: 50, name: '50 por página' },
  { id: ALL_ITEMS_OPTION_ID, name: 'Todos os cargos' },
];

export const DEFAULT_SEARCH_FIELD: TrainingMatrixSearchField = 'cargo';

const SEARCH_FIELDS: TrainingMatrixSearchField[] = ['cargo', 'prefixo', 'titulo'];
const SORT_FIELDS: TrainingMatrixSort[] = ['createdAt', 'name'];
const SORT_ORDERS: TrainingMatrixOrder[] = ['asc', 'desc'];
const DOCUMENT_PREFIXES: TrainingMatrixDocumentPrefix[] = ['MQ', 'PR', 'IT', 'DA'];
const ITEMS_PER_PAGE_OPTIONS = MATRIX_QUANTITY_OPTIONS.map((option) => option.id);

export const TRAINING_MATRIX_ORDER_OPTIONS: OrderOptions[] = [
  { label: 'Por mais recente', sort: 'createdAt', order: 'desc' },
  { label: 'Por mais antigo', sort: 'createdAt', order: 'asc' },
  { label: 'Por ordem alfabética crescente', sort: 'name', order: 'asc' },
  { label: 'Por ordem alfabética decrescente', sort: 'name', order: 'desc' },
];

export function useTrainingMatrixTable() {
  const route = useRoute();
  const router = useRouter();

  const positions = ref<TrainingMatrixPositionRow[]>([]);
  const revision = ref<TrainingMatrixRevisionInfo | null>(null);
  const metadata = ref<TrainingMatrixMetadata | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const page = ref(1);
  const itemsPerPage = ref(10);
  const search = ref('');
  const searchField = ref<TrainingMatrixSearchField>(DEFAULT_SEARCH_FIELD);
  const sort = ref<TrainingMatrixSort | ''>('');
  const order = ref<TrainingMatrixOrder | ''>('');
  const positionId = ref<number | undefined>(undefined);
  const documentPrefix = ref<TrainingMatrixDocumentPrefix | undefined>(undefined);
  const hasHydratedFromUrl = ref(false);
  const allPositions = ref<TrainingMatrixPositionRow[]>([]);
  const hasMorePositions = ref(true);
  const loadingMorePositions = ref(false);
  let currentQueryId = 0;
  let allItemsPage = 1;

  const isAllItemsMode = computed(() => itemsPerPage.value === ALL_ITEMS_OPTION_ID);

  function hydrateFromQuery() {
    const q = route.query;

    page.value = Math.max(1, Number(q.page) || 1);

    const requestedItemsPerPage = Number(q.itemsPerPage);
    itemsPerPage.value = ITEMS_PER_PAGE_OPTIONS.includes(requestedItemsPerPage)
      ? requestedItemsPerPage
      : 10;

    search.value = typeof q.search === 'string' ? q.search : '';

    const requestedSearchField = q.searchField;
    searchField.value = SEARCH_FIELDS.includes(requestedSearchField as TrainingMatrixSearchField)
      ? (requestedSearchField as TrainingMatrixSearchField)
      : DEFAULT_SEARCH_FIELD;

    const requestedSort = q.sort;
    sort.value = SORT_FIELDS.includes(requestedSort as TrainingMatrixSort)
      ? (requestedSort as TrainingMatrixSort)
      : '';

    const requestedOrder = q.order;
    order.value = SORT_ORDERS.includes(requestedOrder as TrainingMatrixOrder)
      ? (requestedOrder as TrainingMatrixOrder)
      : '';

    const requestedPositionId = Number(q.positionId);
    positionId.value = Number.isInteger(requestedPositionId) && requestedPositionId > 0
      ? requestedPositionId
      : undefined;

    const requestedDocumentPrefix = q.documentPrefix;
    documentPrefix.value = DOCUMENT_PREFIXES.includes(
      requestedDocumentPrefix as TrainingMatrixDocumentPrefix
    )
      ? (requestedDocumentPrefix as TrainingMatrixDocumentPrefix)
      : undefined;

    hasHydratedFromUrl.value = true;
  }

  function buildQueryFromState(): Record<string, string> {
    const query: Record<string, string> = {};
    if (page.value > 1) query.page = String(page.value);
    if (itemsPerPage.value !== 10) query.itemsPerPage = String(itemsPerPage.value);
    if (search.value) {
      query.search = search.value;
      query.searchField = searchField.value;
    }
    if (sort.value) query.sort = sort.value;
    if (order.value) query.order = order.value;
    if (positionId.value !== undefined) query.positionId = String(positionId.value);
    if (documentPrefix.value !== undefined) query.documentPrefix = documentPrefix.value;
    return query;
  }

  function syncUrl() {
    if (!hasHydratedFromUrl.value) return;
    router.replace({ path: route.path, query: buildQueryFromState() });
  }

  hydrateFromQuery();

  function buildQueryParams(pageToUse: number, itemsPerPageToUse: number) {
    const trimmedSearch = search.value.trim();
    return {
      page: pageToUse,
      itemsPerPage: itemsPerPageToUse,
      allItems: false,
      search: trimmedSearch || undefined,
      searchField: trimmedSearch ? searchField.value : undefined,
      sort: sort.value || undefined,
      order: order.value || undefined,
      positionId: positionId.value,
      documentPrefix: documentPrefix.value,
    };
  }

  async function fetch() {
    const queryId = ++currentQueryId;
    loading.value = true;
    error.value = null;
    try {
      if (isAllItemsMode.value) {
        allItemsPage = 1;
        const result = await trainingMatrixApi.getCurrent(
          buildQueryParams(allItemsPage, ALL_ITEMS_BATCH_SIZE)
        );
        if (queryId !== currentQueryId) return;
        positions.value = result.data.positions;
        allPositions.value = result.data.positions;
        revision.value = result.data.revision;
        metadata.value = result.metadata;
        hasMorePositions.value = allPositions.value.length < (result.metadata?.total ?? 0);
      } else {
        allPositions.value = [];
        hasMorePositions.value = true;
        const result = await trainingMatrixApi.getCurrent(
          buildQueryParams(page.value, itemsPerPage.value)
        );
        if (queryId !== currentQueryId) return;
        positions.value = result.data.positions;
        revision.value = result.data.revision;
        metadata.value = result.metadata;
      }
    } catch {
      if (queryId !== currentQueryId) return;
      error.value = 'Não foi possível carregar a matriz de treinamentos. Tente novamente.';
    } finally {
      if (queryId === currentQueryId) {
        loading.value = false;
      }
    }
  }

  async function loadMorePositions() {
    if (loadingMorePositions.value || !hasMorePositions.value) return;

    const queryId = currentQueryId;
    loadingMorePositions.value = true;
    try {
      const nextPage = allItemsPage + 1;
      const result = await trainingMatrixApi.getCurrent(
        buildQueryParams(nextPage, ALL_ITEMS_BATCH_SIZE)
      );
      if (queryId !== currentQueryId) return;
      allItemsPage = nextPage;
      allPositions.value = [...allPositions.value, ...result.data.positions];
      metadata.value = result.metadata;
      hasMorePositions.value = allPositions.value.length < (result.metadata?.total ?? 0);
    } catch {
      if (queryId !== currentQueryId) return;
      error.value = 'Não foi possível carregar mais cargos. Tente novamente.';
    } finally {
      if (queryId === currentQueryId) {
        loadingMorePositions.value = false;
      }
    }
  }

  useInfiniteScrollObserver({
    enabled: isAllItemsMode,
    loadingMore: loadingMorePositions,
    hasMore: hasMorePositions,
    onLoadMore: loadMorePositions,
  });

  function changePage(newPage: number) {
    page.value = newPage;
  }

  function changeItemsPerPage(newItemsPerPage: number) {
    itemsPerPage.value = newItemsPerPage;
    page.value = 1;
  }

  function changeSearch(term: string) {
    search.value = term;
    page.value = 1;
  }

  function changeSearchField(field: TrainingMatrixSearchField) {
    searchField.value = field;
    page.value = 1;
  }

  function changeSort(sortCamp: string, orderFlow: string) {
    sort.value = sortCamp as TrainingMatrixSort | '';
    order.value = orderFlow as TrainingMatrixOrder | '';
    page.value = 1;
  }

  function applyFilters(filters: TrainingMatrixFilters) {
    const unchanged =
      positionId.value === filters.positionId && documentPrefix.value === filters.documentPrefix;
    if (unchanged) return;

    positionId.value = filters.positionId;
    documentPrefix.value = filters.documentPrefix;
    page.value = 1;
  }

  watch(
    [page, itemsPerPage, search, searchField, sort, order, positionId, documentPrefix],
    () => {
      syncUrl();
      fetch();
    },
    { immediate: true }
  );

  return {
    positions,
    revision,
    metadata,
    loading,
    error,
    page,
    itemsPerPage,
    search,
    searchField,
    sort,
    order,
    positionId,
    documentPrefix,
    changePage,
    changeItemsPerPage,
    changeSearch,
    changeSearchField,
    changeSort,
    applyFilters,
    fetch,
    allPositions,
    hasMorePositions,
    loadingMorePositions,
    loadMorePositions,
  };
}
